import { Layers2 } from "lucide-react";
import { Fragment } from "react";

import { Badge } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import {
  formatByteSize,
  formatCatalogDate,
  sumTrackDuration,
} from "@/music-catalog/utils/catalog-view";
import { useDisplaySettingsStore } from "@/settings/store/use-display-settings-store";
import { formatAlbumLength } from "@/settings/utils/format-album-length";

import type { LibraryProject } from "@/cloud/types/types";

/**
 * What is true of this project, written as a line rather than as a row of pills.
 *
 * A record sleeve says "12 tracks · 47 min" under the title; it does not put
 * each of those in its own bordered capsule. The catalog grid is the place for
 * badges — there a card has to be scanned against thirteen others, and an icon
 * in a pill is what makes "17" findable at a glance. Here there is one album on
 * screen and the reader is already looking at it, so the same facts belong in
 * the sentence under its name.
 *
 * A duplicate count stays a `Badge`, and is the only thing here that does: it is
 * not a fact about the album but a warning about it, and the one thing on this
 * line the reader might want to act on. Toned only when there is one to find — a
 * permanent amber pill is a warning about nothing.
 */
export const ProjectMetaLine = ({
  duplicateCount,
  project,
}: {
  duplicateCount: number;
  project: LibraryProject;
}) => {
  const { language, t } = useTranslation();
  const albumLengthFormat = useDisplaySettingsStore((state) => state.albumLengthFormat);
  const trackCount = project.trackCount ?? project.tracks.length;
  const length = sumTrackDuration(project.tracks);
  const updated = formatCatalogDate(project.updatedAt, language);

  // Every phrase names what it is rather than relying on an icon to say so, and
  // is assembled through `t` rather than from fragments, so a translator can put
  // the number where their language puts it.
  const facts = [
    t("catalog.trackCount", { count: trackCount }),
    ...(length.seconds > 0
      ? [
          t("catalog.lengthOf", {
            duration: formatAlbumLength({
              format: albumLengthFormat,
              language,
              seconds: length.seconds,
            }),
          }),
        ]
      : []),
    t("catalog.sizeOf", { size: formatByteSize(project.byteLength, language) }),
    ...(updated === undefined ? [] : [t("catalog.updated", { date: updated })]),
  ];

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2 text-sm text-muted-foreground lg:justify-start">
      {facts.map((fact, index) => (
        <Fragment key={fact}>
          {/*
            The separator is drawn rather than written into the text: a middle
            dot inside a translated string is a fragment of a sentence, and the
            first item must not be preceded by one.
          */}
          {index > 0 && (
            <span aria-hidden className="text-muted-foreground/40">
              ·
            </span>
          )}
          <span>{fact}</span>
        </Fragment>
      ))}

      {duplicateCount > 0 && (
        <Badge tone="warning">
          <Layers2 className="size-3" aria-hidden />
          {t("catalog.duplicateCount", { count: duplicateCount })}
        </Badge>
      )}

      {project.forkedFromDesktopProjectId !== null && (
        <Badge tone="neutral">{t("project.forkedFrom")}</Badge>
      )}
    </div>
  );
};
